import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import { isAxiosError } from "axios";
import { twMerge } from "tailwind-merge";
import { Clock, MarkerPin01 } from "untitledui-js/react";

import { Alert } from "@components/Alert";
import { DetailItem } from "@components/VacancyDetailItem";
import { Layout } from "@components/Layout";
import { Loader } from "@components/Loader";
import { VacancyApplyForm } from "@components/VacancyApplyForm";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { useGetVacancy } from "@hooks/useGetVacancy";
import { languageDutch, languageEnglish } from "../i18n";
import { ShareButton } from "@components/ShareButton";
import { sanitizeHtml } from "@utils/sanitizeHtml";

export const VacancyDetailPage = () => {
  const { lang, vacancySlug } = useParams();
  const { t } = useTranslation();
  const currentLang = useCurrentLang(lang);
  const [isApplyFormOpen, setIsApplyFormOpen] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const {
    data: vacancy,
    isLoading,
    error,
  } = useGetVacancy(vacancySlug, {
    locale: currentLang,
  });

  const isNotFound = isAxiosError(error) && error.response?.status === 404;

  const toggleApplyForm = () => setIsApplyFormOpen((prev) => !prev);
  const applyFormSubmitted = () => {
    setIsApplyFormOpen(false);
    setIsFormSubmitted(true);
  };
  const applyButtonLabel = isApplyFormOpen
    ? t("Vacancies.backToVacancy")
    : t("Vacancies.applyNow");

  const translatedSlug =
    currentLang === languageDutch
      ? `/${languageEnglish}/vacancies/${vacancySlug ?? ""}`
      : `/vacancies/${vacancySlug ?? ""}`;

  const hoursDisplay =
    vacancy?.minHours && vacancy.maxHours
      ? `${vacancy.minHours} - ${vacancy.maxHours} ${t("Vacancies.hoursPerWeek")}`
      : "";
  const externalLink = vacancy?.slug
    ? `https://jobsatpon.com/vacature-overzicht-intern#/${currentLang === languageDutch ? languageDutch : languageEnglish}/vacancies/${vacancy.slug}`
    : null;

  return (
    <Layout translatedSlug={translatedSlug}>
      {isLoading && (
        <div className="flex min-h-80 items-center justify-center">
          <Loader />
        </div>
      )}

      {error && !isNotFound && (
        <div className="mx-auto flex min-h-80 max-w-360 items-center justify-center px-4">
          <Alert
            variant="error"
            labelText={t("Vacancies.error.label")}
            text={t("Vacancies.error.text")}
          />
        </div>
      )}

      {!isLoading && (isNotFound || (!error && !vacancy)) && (
        <Navigate to={lang ? `/${lang}/404` : "/404"} replace />
      )}

      {vacancy && (
        <main className="mx-auto w-full max-w-360 pb-8 lg:py-8 lg:py-15">
          <title>{vacancy.title}</title>
          <div
            className={twMerge(
              "mb-8 rounded-sm bg-tertiary-50 px-6.5 pt-6 pb-8 lg:mx-15 lg:mb-15 lg:rounded-sm lg:p-15",
              "dark:bg-secondary-900"
            )}
          >
            <div className="flex flex-col gap-4">
              <Typography
                as="h1"
                className={twMerge(
                  "text-3xl leading-[120%] text-secondary-800",
                  "dark:text-white"
                )}
              >
                {vacancy.title}
              </Typography>

              <div className="flex flex-col gap-2 lg:flex-row lg:gap-8">
                {vacancy.company.name && (
                  <DetailItem image={vacancy.company.logo}>
                    {vacancy.company.name}
                  </DetailItem>
                )}
                {vacancy.location && (
                  <DetailItem icon={MarkerPin01}>{vacancy.location}</DetailItem>
                )}
                {hoursDisplay && (
                  <DetailItem icon={Clock}>{hoursDisplay}</DetailItem>
                )}
              </div>

              <Button
                id="vacancy-apply-button"
                variant="primary"
                size="xl"
                className="w-fit gap-2 lg:hidden"
                onClick={toggleApplyForm}
                aria-expanded={isApplyFormOpen}
              >
                {applyButtonLabel}
              </Button>
            </div>
          </div>

          {vacancy.description && (
            <div className="grid grid-cols-1 lg:mx-15 lg:grid-cols-3">
              <div className="flex flex-col gap-4 px-6.5 pb-8 lg:col-span-2 lg:px-15 lg:pb-15">
                <div className="flex items-center justify-between gap-4">
                  <Typography
                    className={twMerge(
                      "text-tertiary-500",
                      "dark:text-tertiary-300"
                    )}
                  >{`${t("Vacancies.vacancy")} • ${vacancy.title}`}</Typography>
                  <ShareButton text={t("Share.shareButton")} />
                </div>
                {isApplyFormOpen ? (
                  <VacancyApplyForm
                    formId={vacancy.guid}
                    locale={currentLang}
                    applyFormSubmitted={applyFormSubmitted}
                  />
                ) : (
                  <>
                    {isFormSubmitted && (
                      <div className="mb-4 flex items-center justify-center">
                        <Alert
                          variant="success"
                          labelText={t("Vacancies.form.submitSuccess.label")}
                          text={t("Vacancies.form.submitSuccess.text")}
                        />
                      </div>
                    )}
                    <div
                      className="richText"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(vacancy.description),
                      }}
                    />
                  </>
                )}
              </div>

              {/* Hide form button when it's already submitted */}
              {!isFormSubmitted && (
                <div
                  className={twMerge(
                    "mx-6.5 flex h-fit flex-col gap-4 self-start rounded-sm bg-tertiary-50 p-8 lg:sticky lg:top-24 lg:mx-0",
                    "dark:bg-secondary-900"
                  )}
                >
                  <Typography as="h2" className="dark:text-white">
                    {t("Vacancies.interested")}
                  </Typography>
                  <Button
                    id="vacancy-apply-button-sidebar"
                    variant="primary"
                    size="xl"
                    className="w-fit gap-2 md:flex"
                    onClick={toggleApplyForm}
                    aria-expanded={isApplyFormOpen}
                  >
                    {applyButtonLabel}
                  </Button>
                  {externalLink && (
                    <a
                      href={externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-700 underline underline-offset-2 transition-colors duration-200 ease-in-out dark:text-primary-500"
                      data-testid="vacancy-view-external-link"
                    >
                      {t("Vacancies.viewOnJobsAtPon")}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      )}
    </Layout>
  );
};
